import { useMemo } from 'react';
import { useBookmarkSelection } from '../contexts/BookmarkSelectionContext';
import type {
  BatchActionTarget,
  BookmarkListBatchMeta,
} from '../components/BatchOperationsBar/types';
import type { BookmarkSectionBatchTarget, ViewMode } from '../types/api';
import type { PageInfo } from '../utils/pageInfo';
import {
  bookmarkSectionBatchTargetsEqual,
  buildBookmarkSectionBatchTarget,
} from '../utils/bookmarkSectionActionParams';

export interface UseBatchOperationTargetInput {
  viewMode: ViewMode;
  bookmarkListBatchMeta: BookmarkListBatchMeta | null;
  pageInfo: PageInfo | null;
}

export interface UseBatchOperationTargetResult {
  isSectionBatch: boolean;
  section: BookmarkSectionBatchTarget | null;
  bookmarkIds: string[];
  batchTarget: BatchActionTarget;
  canRunBatch: boolean;
  effectiveCount: number;
}

export function useBatchOperationTarget(
  input: UseBatchOperationTargetInput,
): UseBatchOperationTargetResult {
  const { viewMode, bookmarkListBatchMeta, pageInfo } = input;
  const { selectedBookmarkIds, sectionBatchTarget } = useBookmarkSelection();

  const sectionDescriptor = useMemo(
    () =>
      bookmarkListBatchMeta
        ? buildBookmarkSectionBatchTarget({
            viewMode,
            sort: bookmarkListBatchMeta.sort,
            folderId: bookmarkListBatchMeta.folderId,
            tagApiParam: bookmarkListBatchMeta.tagSlug,
          })
        : null,
    [viewMode, bookmarkListBatchMeta],
  );

  const isSectionBatch = Boolean(
    sectionBatchTarget &&
      sectionDescriptor &&
      bookmarkSectionBatchTargetsEqual(sectionBatchTarget, sectionDescriptor),
  );

  const section = isSectionBatch ? sectionBatchTarget : null;
  const bookmarkIds = Array.from(selectedBookmarkIds);

  const batchTarget = useMemo((): BatchActionTarget => {
    if (isSectionBatch && section) {
      return { kind: 'section', section };
    }
    return { kind: 'ids', bookmarkIds };
  }, [isSectionBatch, section, bookmarkIds]);

  const canRunBatch = isSectionBatch || bookmarkIds.length > 0;

  const effectiveCount =
    isSectionBatch && typeof pageInfo?.count === 'number'
      ? pageInfo.count
      : bookmarkIds.length;

  return {
    isSectionBatch,
    section,
    bookmarkIds,
    batchTarget,
    canRunBatch,
    effectiveCount,
  };
}
